import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import type { FallbackProps } from 'react-error-boundary'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Modal } from '~/components/Global/Design/Modal/Modal'
import {
  content as contentModalStyle,
  inner as innerModalStyle
} from '~/components/Global/Design/Modal/Modal.css'
import { ErrorDetails } from '~/components/Global/Elements/ErrorDetails/ErrorDetails'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { CloseTrigger } from '~/components/Global/Interactions/CloseTrigger/CloseTrigger'
import { applicantAccessDeniedMessage } from '~/domains/applicants/accessDenied'
import { themeClass } from '~/styles/theme.css'

interface ApplicantModalErrorFallbackProps extends FallbackProps {
  onClose: () => void
  errorType?: 'forbidden' | 'not-found' | 'error'
  agencies?: Array<{ id: number; name: string }>
}

export const ApplicantModalErrorFallback = ({
  error,
  resetErrorBoundary,
  onClose,
  errorType = 'error',
  agencies
}: ApplicantModalErrorFallbackProps): ReactElement => {
  const handleRetry = () => {
    resetErrorBoundary()
  }

  const handleClose = () => {
    resetErrorBoundary()
    onClose()
  }

  let title = 'Une erreur est survenue'
  let message =
    "Une erreur inattendue s'est produite lors de l'affichage de ce candidat."

  if (errorType === 'not-found') {
    title = 'Candidat non trouvé'
    message = "Ce candidat n'existe pas ou a été supprimé."
  } else if (errorType === 'forbidden') {
    title = 'Accès refusé'
    message = applicantAccessDeniedMessage(agencies)
  }

  return (
    <Modal onClose={handleClose} open={true}>
      <div className={clsx(themeClass)}>
        <div className={clsx(innerModalStyle)}>
          <CloseTrigger onClose={handleClose} />
          <section className={contentModalStyle}>
            <NoResult
              icon={faExclamationTriangle}
              isError={true}
              message={title}
              description={message}
            />
            {errorType === 'error' && error ? (
              <ErrorDetails error={error} />
            ) : null}
            <ButtonGroup align="center">
              {errorType === 'error' && (
                <Button
                  variation={ButtonVariation.Primary}
                  onClick={handleRetry}
                >
                  Réessayer
                </Button>
              )}
              <Button
                variation={ButtonVariation.Secondary}
                onClick={handleClose}
              >
                Fermer
              </Button>
            </ButtonGroup>
          </section>
        </div>
      </div>
    </Modal>
  )
}
